import React, { useContext, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig from "../siteConfig";
import "../css/checkout.css";

const countries = [
  "Australia",
  "Austria",
  "Belgium",
  "Canada",
  "Denmark",
  "Finland",
  "France",
  "Germany",
  "Hong Kong",
  "Ireland",
  "Italy",
  "Japan",
  "Luxembourg",
  "Malaysia",
  "Netherlands",
  "New Zealand",
  "Norway",
  "Portugal",
  "Singapore",
  "South Korea",
  "Spain",
  "Sweden",
  "Switzerland",
  "Thailand",
  "United Arab Emirates",
  "United Kingdom",
  "United States",
];
const initialForm = {
  contact: "",
  country: "Hong Kong",
  firstName: "",
  lastName: "",
  company: "",
  address: "",
  apartment: "",
  city: "",
  state: "",
  postalCode: "",
};

export default function CheckOut() {
  const { cartItems } = useContext(CartContext);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const items = useMemo(
    () =>
      cartItems
        .map((item) => ({
          ...item,
          product: productData.find((p) => p.id === item.id),
        }))
        .filter((item) => item.product),
    [cartItems]
  );
  const subtotal = items.reduce(
    (sum, item) => sum + Number(item.product.price) * item.quantity,
    0
  );
  const shipping = siteConfig.shippingRate;
  const total = subtotal + shipping;

  const update = ({ target: { name, value } }) => {
    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: "" }));
  };

  const submit = (event) => {
    event.preventDefault();
    const required = [
      "contact",
      "country",
      "firstName",
      "lastName",
      "address",
      "city",
      "state",
      "postalCode",
    ];
    const nextErrors = required.reduce(
      (result, name) =>
        form[name].trim()
          ? result
          : { ...result, [name]: "This field is required" },
      {}
    );
    if (form.contact && !/^\S+@\S+\.\S+$/.test(form.contact))
      nextErrors.contact = "Enter a valid email address";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    setSubmitting(true);
    const orderNo = `${Date.now()}${Math.floor(
      100000 + Math.random() * 900000
    )}`;
    const origin = window.location.origin;
    const params = new URLSearchParams({
      orderNo,
      amount: total.toFixed(2),
      currency: siteConfig.currency,
      email: form.contact,
      success_url: `${origin}/payment?state=success`,
      cancel_url: `${origin}/payment?state=cancel`,
      title: items[0].product.name,
    });
    window.location.assign(`${origin}/create_payment.php?${params.toString()}`);
  };

  if (!items.length)
    return (
      <main className="checkout-empty">
        <h1>Your cart is empty</h1>
        <Link to="/shop">Continue shopping</Link>
      </main>
    );
  const field = (name, placeholder, autoComplete) => (
    <label className={`checkout-field ${errors[name] ? "has-error" : ""}`}>
      <span className="sr-only">{placeholder}</span>
      <input
        name={name}
        value={form[name]}
        onChange={update}
        placeholder={placeholder}
        autoComplete={autoComplete}
      />
      {errors[name] && <small>{errors[name]}</small>}
    </label>
  );

  return (
    <main className="checkout-page">
      <form className="checkout-form" onSubmit={submit} noValidate>
        <div className="checkout-form__inner">
          <Link to="/" className="checkout-wordmark">
            <img src={siteConfig.logo} alt={siteConfig.brandName} />
          </Link>
          <section>
            <h2>Contact</h2>
            {field("contact", "Email", "email")}
          </section>
          <section>
            <h2>Delivery</h2>
            <label className="checkout-field">
              <span>Country / Region</span>
              <select name="country" value={form.country} onChange={update}>
                {countries.map((country) => (
                  <option key={country}>{country}</option>
                ))}
              </select>
            </label>
            <div className="checkout-row">
              {field("firstName", "First name", "given-name")}
              {field("lastName", "Last name", "family-name")}
            </div>
            {field("company", "Company (optional)", "organization")}
            {field("address", "Address", "street-address")}
            {field(
              "apartment",
              "Apartment, suite, etc. (optional)",
              "address-line2"
            )}
            <div className="checkout-row three">
              {field("city", "City", "address-level2")}
              {field("state", "State / Region", "address-level1")}
              {field("postalCode", "Postal code", "postal-code")}
            </div>
          </section>
          <section>
            <h2>Shipping method</h2>
            <div className="shipping-method">
              <span>Standard shipping</span>
              <strong>${shipping.toFixed(2)}</strong>
            </div>
          </section>
          <section>
            <h2>Payment</h2>
            <p className="section-help">
              After you select Pay now, you will continue to our secure payment
              provider. Card details are never entered on this website.
            </p>
          </section>
          <button className="pay-button" type="submit" disabled={submitting}>
            {submitting ? "Redirecting…" : `Pay now · $${total.toFixed(2)}`}
          </button>
          <nav className="checkout-policies">
            <Link to="/returns">Refund policy</Link>
            <Link to="/shipping">Shipping</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/cookies">Cookies</Link>
            <Link to="/terms">Terms</Link>
            <Link to="/accessibility">Accessibility</Link>
          </nav>
        </div>
      </form>
      <aside className="checkout-summary">
        <div className="checkout-summary__inner">
          <div className="checkout-items">
            {items.map((item) => (
              <div
                className="checkout-item"
                key={`${item.id}-${item.size}-${item.color}`}
              >
                <div className="checkout-item__image">
                  <img src={item.product.image} alt="" />
                  <span>{item.quantity}</span>
                </div>
                <div>
                  <h3>{item.product.name}</h3>
                  <p>{item.size !== "One size" ? item.size : ""}</p>
                </div>
                <strong>
                  ${(Number(item.product.price) * item.quantity).toFixed(2)}
                </strong>
              </div>
            ))}
          </div>
          <div className="checkout-totals">
            <p>
              <span>Subtotal</span>
              <span>${subtotal.toFixed(2)}</span>
            </p>
            <p>
              <span>Shipping</span>
              <span>${shipping.toFixed(2)}</span>
            </p>
            <div>
              <span>
                Total <small>USD</small>
              </span>
              <strong>${total.toFixed(2)}</strong>
            </div>
          </div>
        </div>
      </aside>
    </main>
  );
}
